import { Link } from "react-router-dom";
import { useGlobal } from "reactn";
import classNames from "classnames";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import { BasicChampionPerformanceWithTier } from "@ugitgud/legos/queries/lol";
import { getRoleLabel, getRevApiRole, getRoleSVG, normalizeRole, RoleS } from "@ugg/shared/utils/role-helpers";
import { getChampionBuildUrl, getSpecialGameModeChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { buildQueryParams } from "@ugg/shared/utils/url-query-params";
import { useQueryString } from "@ugg/shared/hooks/use-query-string";
import { SPECIAL_GAME_MODES } from "@ugg/shared/utils/special-game-modes";
import { getTierColor, getTierGradeColorHex, TIER_GRADE_STYLES, ALL_TIERS, TierGrade } from "@ugg/shared/utils/tier-helpers";

const LANE_ROLES = new Set(["Top", "Mid", "Bottom"]);

interface PlusTierListVisualizationProps {
  className?: string;
  stats: (BasicChampionPerformanceWithTier | null)[];
  username: string;
  filters: {
    role?: string;
    queueType?: string;
  };
  seasonContext?: string;
}

interface ProfileChampionCardProps {
  stat: BasicChampionPerformanceWithTier;
  role?: string;
  tierColor: string;
  interactive?: boolean;
}

export function ProfileChampionCard({ stat, role, tierColor, interactive = true }: ProfileChampionCardProps) {
  const [language] = useGlobal("language");
  const { getChampionName, getNormalizedChampionName, getChampionImgFromSprite } = getRiotAssetsContext();
  const params = useQueryString();
  const { role: _role, ...filteredParams } = params;

  const championId = stat.championId!;
  const winRate = ((stat.wins ?? 0) / (stat.totalMatches || 1)) * 100;
  const hasRole = role && role !== RoleS.NONE && role !== RoleS.ALL;
  const showRoleBadge = role && role !== RoleS.NONE;

  // Special game modes (e.g. ARAM) have a dedicated build URL (/lol/champions/aram/:champion-aram).
  // The queue lives in the path, so don't fall back to the generic build page with a ?queueType query.
  const specialMode = Object.values(SPECIAL_GAME_MODES).find((mode) => mode.queueType === params.queueType);

  let pathname: string;
  let search: string;
  if (specialMode) {
    pathname = getSpecialGameModeChampionBuildUrl(getNormalizedChampionName(championId), specialMode.urlName, { language });
    const { queueType: _queueType, ...rest } = filteredParams;
    search = buildQueryParams(rest);
  } else {
    pathname = getChampionBuildUrl(getNormalizedChampionName(championId), {
      language,
      ...(hasRole && { role: getRevApiRole(role) }),
    });
    search = buildQueryParams(filteredParams);
  }

  const cardClassName =
    "group flex flex-col items-center justify-center gap-1 rounded w-full h-[100px] xs:h-[112px] max-w-[84px] xs:max-h-[112px] bg-purple-400 shadow-[inset_0_1px_0_0_#32326E]";

  const cardContent = (
    <>
      <div className="relative shrink-0">
        <div className="rounded-full w-[48px] h-[48px] p-0.5 bg-purple-200 border" style={{ borderColor: tierColor }}>
          <div className="relative rounded-full overflow-hidden w-full h-full [&>div]:absolute [&>div]:top-1/2 [&>div]:left-1/2 [&>div]:-translate-x-1/2 [&>div]:-translate-y-1/2 [&>div]:scale-110">
            {getChampionImgFromSprite(championId, { size: 48 })}
          </div>
        </div>
        {showRoleBadge && (
          <div className="absolute right-0 bottom-0 flex rounded-sm items-center justify-center bg-purple-200 w-3.5 h-3.5">
            <div className="w-[10px] h-[10px] [&>svg]:w-full [&>svg]:h-full">{getRoleSVG(role)}</div>
          </div>
        )}
      </div>

      <span
        className={classNames(
          "hidden xs:block text-[12px] font-semibold text-white text-center leading-tight px-1 w-full truncate",
          interactive && "group-hover:underline",
        )}
      >
        {getChampionName(championId)}
      </span>

      <div className="hidden xs:block w-[60px] h-px bg-purple-100" />

      <div className="flex flex-col items-center gap-0">
        <span className="text-[10px] font-bold leading-tight">
          <span className={getTierColor(winRate)}>{winRate.toFixed(1)}%</span>
          <span className="ml-1 text-lavender-50">WR</span>
        </span>
        <span className="text-[10px] text-accent-gray-100 leading-tight">{stat.totalMatches} games</span>
      </div>
    </>
  );

  if (!interactive) {
    return <div className={cardClassName}>{cardContent}</div>;
  }

  return (
    <Link className={cardClassName} to={{ pathname, search }}>
      {cardContent}
    </Link>
  );
}

export function PlusTierListVisualization({
  className,
  stats,
  username,
  filters,
  seasonContext,
}: PlusTierListVisualizationProps) {
  const qualified = stats.filter((s): s is BasicChampionPerformanceWithTier => s !== null);

  if (qualified.length === 0) {
    return (
      <div className="w-full flex items-center justify-center h-[300px] rounded-[3px] bg-purple-200">
        <span className="text-lavender-50 text-[16px]">No data available</span>
      </div>
    );
  }

  const grouped = ALL_TIERS.reduce<Record<TierGrade, BasicChampionPerformanceWithTier[]>>((acc, tier) => {
    acc[tier] = [];
    return acc;
  }, {} as Record<TierGrade, BasicChampionPerformanceWithTier[]>);

  for (const stat of qualified) {
    const tier = stat.tier as TierGrade;
    if (tier && grouped[tier]) grouped[tier].push(stat);
  }

  for (const tier of ALL_TIERS) {
    grouped[tier].sort((a, b) => {
      const wrA = ((a.wins ?? 0) / (a.totalMatches || 1)) * 100;
      const wrB = ((b.wins ?? 0) / (b.totalMatches || 1)) * 100;
      return wrB - wrA;
    });
  }

  const roleLabel = (() => {
    if (!filters.role || filters.role === RoleS.NONE) return "";
    const label = getRoleLabel(filters.role);
    return label ? ` for ${LANE_ROLES.has(label) ? `${label} Lane` : label}` : "";
  })();

  return (
    <div className={classNames("plus-tier-list-visualization rounded p-[18px] bg-purple-200", className)}>
      <div className="flex items-center gap-2 border-l-[3px] border-accent-blue-400 pl-2">
        <span className="text-white font-bold text-[14px]">PLUS Tier List</span>
        <span className="text-lavender-300 text-[14px]">{`${username}'s best champions${roleLabel}${
          seasonContext ? ` in ${seasonContext}` : ""
        }`}</span>
      </div>

      <div className="grid grid-cols-3 gap-[18px] pt-5">
        {ALL_TIERS.map((tier) => {
          const color = getTierGradeColorHex(tier);

          return (
            <div
              key={tier}
              className={classNames(
                "rounded p-[18px] min-h-[246px] xs:min-h-[270px] md:min-h-[278px] relative bg-purple-400 shadow-[0_2px_2px_0_rgba(0,0,0,0.4)] border-t",
                TIER_GRADE_STYLES[tier].cardClass,
              )}
              style={{ backgroundImage: `radial-gradient(ellipse at top left, ${color}1A 0%, ${color}0A 40%, transparent 100%)` }}
            >
              <div
                className={classNames(
                  "absolute -top-[13px] left-1/2 -translate-x-1/2 px-2.5 py-1 rounded text-[12px] font-bold whitespace-nowrap leading-tight border",
                  TIER_GRADE_STYLES[tier].badgeClass,
                )}
              >
                {tier}
              </div>

              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2 md:gap-4 justify-items-center">
                {grouped[tier].map((stat) => (
                  <ProfileChampionCard
                    key={stat.championId}
                    stat={stat}
                    role={stat.role != null ? normalizeRole(stat.role) : undefined}
                    tierColor={color}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
